import React, { useState } from 'react';
import { View, Text, Platform, Pressable, ScrollView } from 'react-native';
import { useStyles } from '../../hooks/useStyles';
import { addColorOpacity } from '../../shared/utils/styles';
import { Feedback, FeedbackType, FeedbackStatus, FeedbackPriority } from '../../shared/types/feedback';
import { Badge } from '../ui/Badge';
import { Ionicons } from '@expo/vector-icons';

// Cross-platform clipboard helper
const copyToClipboard = async (text: string): Promise<boolean> => {
  if (Platform.OS === 'web') {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  }
  // For native, we'd use expo-clipboard but for now just return false
  return false;
};

export interface FeedbackItemCardProps {
  feedback: Feedback;
  isAdmin?: boolean;
  onPress?: (feedback: Feedback) => void;
}

const FEEDBACK_TYPE_LABELS: Record<FeedbackType, string> = {
  bug: 'BUG',
  feature_request: 'FEATURE',
  ui_ux: 'UI/UX',
  performance: 'PERF',
  accessibility: 'A11Y',
  general: 'GENERAL',
  other: 'OTHER',
};

const STATUS_LABELS: Record<FeedbackStatus, string> = {
  submitted: 'Submitted',
  acknowledged: 'Acknowledged',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

const PRIORITY_LABELS: Record<FeedbackPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

const getStatusVariant = (status: FeedbackStatus): 'default' | 'primary' | 'success' | 'subtle' => {
  switch (status) {
    case 'submitted':
      return 'default';
    case 'acknowledged':
    case 'in_progress':
      return 'primary';
    case 'resolved':
      return 'success';
    case 'closed':
      return 'subtle';
    default:
      return 'default';
  }
};

const getPriorityVariant = (priority: FeedbackPriority): 'default' | 'primary' | 'warning' | 'error' => {
  switch (priority) {
    case 'low':
      return 'default';
    case 'medium':
      return 'primary';
    case 'high':
      return 'warning';
    case 'critical':
      return 'error';
    default:
      return 'default';
  }
};

// Helper to format a single feedback item as text
export function formatFeedbackAsText(feedback: Feedback): string {
  const date = new Date(feedback.created_at).toLocaleString();
  const lines = [
    `[${FEEDBACK_TYPE_LABELS[feedback.type]}] ${feedback.title || 'Feedback'}`,
    `Status: ${STATUS_LABELS[feedback.status]} | Priority: ${PRIORITY_LABELS[feedback.priority]}`,
    `Date: ${date}`,
  ];
  if (feedback.current_page) {
    lines.push(`Page: ${feedback.current_page}`);
  }
  if (feedback.user_email) {
    lines.push(`User: ${feedback.user_email}`);
  }
  lines.push('', feedback.description);
  if (feedback.admin_notes) {
    lines.push('', `Admin Notes: ${feedback.admin_notes}`);
  }
  return lines.join('\n');
}

export function FeedbackItemCard({ feedback, isAdmin = false, onPress }: FeedbackItemCardProps) {
  const styles = useStyles();
  const { tokens, utils } = styles;
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Check if description is long enough to need expansion
  const isLongDescription = feedback.description.length > 150;

  const handleCopy = async () => {
    const text = formatFeedbackAsText(feedback);
    const success = await copyToClipboard(text);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const cardContent = (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: utils.spacing.md,
        padding: utils.spacing.md,
        borderRadius: utils.radius.md,
        backgroundColor: tokens.color.surface,
        borderWidth: 1,
        borderColor: tokens.color.border,
        ...Platform.select({
          web: {
            boxSizing: 'border-box' as any,
          },
        }),
      }}
    >
      {/* Left: Content */}
      <View style={{ flex: 1, minWidth: 0 }}>
        {/* Type Badge */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: utils.spacing.sm, marginBottom: utils.spacing.xs }}>
          <Badge 
            label={FEEDBACK_TYPE_LABELS[feedback.type]} 
            variant="primary" 
            size="xs"
          />
          {feedback.current_page && (
            <Text
              style={{
                fontSize: utils.typography.sizes.xs,
                color: addColorOpacity(tokens.color.onSurface, 0.5),
                fontFamily: utils.typography.body.fontFamily,
              }}
              numberOfLines={1}
            >
              {feedback.current_page}
            </Text>
          )}
        </View>
        
        {/* Description - smaller text, expandable */}
        <Pressable 
          onPress={() => isLongDescription && setExpanded(!expanded)}
          style={{ marginBottom: utils.spacing.xs }}
        >
          {expanded ? (
            <ScrollView 
              style={{ maxHeight: 200 }}
              nestedScrollEnabled
            >
              <Text
                style={{
                  fontSize: utils.typography.sizes.sm,
                  fontWeight: utils.typography.weights.regular,
                  color: tokens.color.onSurface,
                  fontFamily: utils.typography.body.fontFamily,
                  lineHeight: utils.typography.sizes.sm * 1.5,
                }}
              >
                {feedback.description}
              </Text>
            </ScrollView>
          ) : (
            <Text
              style={{
                fontSize: utils.typography.sizes.sm,
                fontWeight: utils.typography.weights.regular,
                color: tokens.color.onSurface,
                fontFamily: utils.typography.body.fontFamily,
                lineHeight: utils.typography.sizes.sm * 1.5,
              }}
              numberOfLines={4}
            >
              {feedback.description}
            </Text>
          )}
          {isLongDescription && (
            <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: utils.spacing.xs }}>
              <Ionicons 
                name={expanded ? 'chevron-up' : 'chevron-down'} 
                size={14} 
                color={tokens.color.primary} 
              />
              <Text
                style={{
                  fontSize: utils.typography.sizes.xs,
                  color: tokens.color.primary,
                  fontFamily: utils.typography.body.fontFamily,
                  marginLeft: 2,
                }}
              >
                {expanded ? 'Show less' : 'Show more'}
              </Text>
            </View>
          )}
        </Pressable>

        {/* Metadata Row: Status, Priority, Date, User */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: utils.spacing.sm,
            flexWrap: 'wrap',
            marginTop: utils.spacing.xs,
          }}
        >
          <Badge
            label={STATUS_LABELS[feedback.status]}
            variant={getStatusVariant(feedback.status)}
            size="xs"
          />
          <Badge
            label={PRIORITY_LABELS[feedback.priority]}
            variant={getPriorityVariant(feedback.priority)}
            size="xs"
          />
          <Text
            style={{
              fontSize: utils.typography.sizes.xs,
              color: addColorOpacity(tokens.color.onSurface, 0.5),
              fontFamily: utils.typography.body.fontFamily,
            }}
          >
            {formatDate(feedback.created_at)}
          </Text>
          {isAdmin && feedback.user_email && (
            <Text
              style={{
                fontSize: utils.typography.sizes.xs,
                color: addColorOpacity(tokens.color.onSurface, 0.5),
                fontFamily: utils.typography.body.fontFamily,
              }}
              numberOfLines={1}
            >
              {feedback.user_email.split('@')[0]}
            </Text>
          )}
        </View>

        {/* Admin Notes */}
        {isAdmin && feedback.admin_notes && (
          <View
            style={{
              marginTop: utils.spacing.sm,
              padding: utils.spacing.sm,
              backgroundColor: addColorOpacity(tokens.color.primary, 0.05),
              borderRadius: utils.radius.sm,
              borderLeftWidth: 2,
              borderLeftColor: tokens.color.primary,
            }}
          >
            <Text
              style={{
                fontSize: utils.typography.sizes.xs,
                color: tokens.color.onSurface,
                fontWeight: utils.typography.weights.semibold,
                marginBottom: 2,
                fontFamily: utils.typography.body.fontFamily,
              }}
            >
              Admin Notes
            </Text>
            <Text
              style={{
                fontSize: utils.typography.sizes.xs,
                color: tokens.color.onBackground,
                lineHeight: utils.typography.sizes.xs * 1.4,
                fontFamily: utils.typography.body.fontFamily,
              }}
              numberOfLines={2}
            >
              {feedback.admin_notes}
            </Text>
          </View>
        )}
      </View>
      
      {/* Copy Button */}
      <Pressable
        onPress={handleCopy}
        style={{
          padding: utils.spacing.xs,
          borderRadius: utils.radius.sm,
          backgroundColor: copied ? addColorOpacity(tokens.color.success, 0.1) : 'transparent',
        }}
      >
        <Ionicons
          name={copied ? 'checkmark' : 'copy-outline'}
          size={18}
          color={copied ? tokens.color.success : addColorOpacity(tokens.color.onSurface, 0.5)}
        />
      </Pressable>
    </View>
  );

  if (onPress) {
    return (
      <Pressable
        onPress={() => onPress(feedback)}
        style={({ pressed }) => ({
          opacity: pressed ? 0.7 : 1,
        })}
      >
        {cardContent}
      </Pressable>
    );
  }

  return cardContent;
}
